import React from 'react';
import { Editor } from '@tiptap/react';
import Icon, { IconName } from '../Icon';
import { useTranslation } from '../../i18n';
import './RichEditor.css';

interface ToolbarProps {
  editor: Editor | null;
}

type ToolbarButton = {
  icon: IconName;
  label: string;
  action: () => void;
  isActive?: () => boolean;
  type?: 'button' | 'separator';
};

const Toolbar: React.FC<ToolbarProps> = ({ editor }) => {
  const { t } = useTranslation();
  if (!editor) return null;

  const addImage = () => {
    const url = window.prompt(t('richEditor.imageUrlPrompt'));
    if (url) {
      editor.chain().focus().setImage({ src: url }).run();
    }
  };

  const addLink = () => {
    const previousUrl = editor.getAttributes('link').href;
    const url = window.prompt(t('richEditor.linkUrlPrompt'), previousUrl || 'https://');
    if (url === null) return;
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  };

  const insertTable = () => {
    editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run();
  };

  const buttons: (ToolbarButton | { type: 'separator' })[] = [
    { icon: 'bold', label: t('richEditor.bold'), action: () => editor.chain().focus().toggleBold().run(), isActive: () => editor.isActive('bold') },
    { icon: 'italic', label: t('richEditor.italic'), action: () => editor.chain().focus().toggleItalic().run(), isActive: () => editor.isActive('italic') },
    { icon: 'strikethrough', label: t('richEditor.strike'), action: () => editor.chain().focus().toggleStrike().run(), isActive: () => editor.isActive('strike') },
    { type: 'separator' },
    { icon: 'heading', label: t('richEditor.heading2'), action: () => editor.chain().focus().toggleHeading({ level: 2 }).run(), isActive: () => editor.isActive('heading', { level: 2 }) },
    { icon: 'heading', label: t('richEditor.heading3'), action: () => editor.chain().focus().toggleHeading({ level: 3 }).run(), isActive: () => editor.isActive('heading', { level: 3 }) },
    { type: 'separator' },
    { icon: 'bulletList', label: t('richEditor.bulletList'), action: () => editor.chain().focus().toggleBulletList().run(), isActive: () => editor.isActive('bulletList') },
    { icon: 'orderedList', label: t('richEditor.orderedList'), action: () => editor.chain().focus().toggleOrderedList().run(), isActive: () => editor.isActive('orderedList') },
    { icon: 'taskList', label: t('richEditor.taskList'), action: () => editor.chain().focus().toggleTaskList().run(), isActive: () => editor.isActive('taskList') },
    { type: 'separator' },
    { icon: 'link', label: t('richEditor.link'), action: addLink, isActive: () => editor.isActive('link') },
    { icon: 'image', label: t('richEditor.image'), action: addImage },
    { icon: 'codeBlock', label: t('richEditor.codeBlock'), action: () => editor.chain().focus().toggleCodeBlock().run(), isActive: () => editor.isActive('codeBlock') },
    { icon: 'blockquote', label: t('richEditor.blockquote'), action: () => editor.chain().focus().toggleBlockquote().run(), isActive: () => editor.isActive('blockquote') },
    { type: 'separator' },
    { icon: 'table', label: t('richEditor.table'), action: insertTable },
    { icon: 'horizontalRule', label: t('richEditor.divider'), action: () => editor.chain().focus().setHorizontalRule().run() },
  ];

  return (
    <div className="re-toolbar" role="toolbar" aria-label={t('richEditor.toolbar')}>
      {buttons.map((btn, i) => {
        if (btn.type === 'separator') {
          return <div key={`sep-${i}`} className="re-toolbar__sep" />;
        }
        const b = btn as ToolbarButton;
        const active = b.isActive?.() || false;
        return (
          <button
            key={b.label}
            type="button"
            className={`re-toolbar__btn ${active ? 'active' : ''}`}
            onClick={b.action}
            title={b.label}
            aria-label={b.label}
            aria-pressed={active}
          >
            <Icon name={b.icon} />
          </button>
        );
      })}
    </div>
  );
};

export default Toolbar;
